/**
 * Excepciones por contrato: descuentos y acuerdos especiales con el locatario que cambian lo
 * que se cobra respecto de las condiciones comerciales. Se aplican en dos momentos:
 *
 * - Antes del cálculo (`ajustarDivisiones`): descuento en % y "no cobrar" rebajan la tarifa o
 *   el % de la división, así la publicidad sobre el arriendo sale del monto ya rebajado;
 *   "solo variable" deja el variable sin el fijo como mínimo.
 * - Después del cálculo (`aplicarExcepciones`): descuento en monto, monto pactado y el fijo en
 *   $0 de "solo variable". La publicidad se mantiene sobre el fijo del contrato, como hoy en Softland.
 *
 * Toda excepción aplicada queda escrita en la glosa del ítem, con su motivo. "No cobrar" el fijo
 * perdona el cargo pero no el mínimo: OPCION sigue comparando el variable con el fijo del contrato.
 */
import { DivisionCalculo, ItemCalculado, formatearNumero, normalizar, promocionSobreAlquiler } from "@/helpers/facturacion";

export type TipoExcepcion = "descuento-pct" | "descuento-monto" | "no-cobrar" | "solo-variable" | "monto-pactado";
export type ConceptoExcepcion = "ALQUILER FIJO" | "ALQUILER VARIABLE" | "GASTOS COMUNES" | "FDO. PROMOCION";

export interface Excepcion {
  id?: string;
  codigoContrato: number | string;
  tipo: TipoExcepcion;
  /** Concepto al que se aplica; "solo variable" afecta al arriendo (fijo y variable). */
  concepto: ConceptoExcepcion;
  /** Descuento en %: fracción (0,25 = 25%). Descuento en monto o monto pactado: pesos. */
  valor: number;
  desde: string; // YYYY-MM
  hasta: string; // YYYY-MM, o "" si no tiene término
  motivo: string;
  autorizadoPor: string;
  creadoPor?: string;
  creadoEn?: string;
}

export const TIPOS_EXCEPCION: Array<{ value: TipoExcepcion; text: string }> = [
  { value: "descuento-pct", text: "Descuento en %" },
  { value: "descuento-monto", text: "Descuento en monto ($)" },
  { value: "no-cobrar", text: "No cobrar" },
  { value: "solo-variable", text: "Solo variable (sin el fijo como mínimo)" },
  { value: "monto-pactado", text: "Monto pactado ($)" },
];

export const CONCEPTOS_EXCEPCION: Array<{ value: ConceptoExcepcion; text: string }> = [
  { value: "ALQUILER FIJO", text: "Arriendo fijo" },
  { value: "ALQUILER VARIABLE", text: "Arriendo variable" },
  { value: "GASTOS COMUNES", text: "Gastos comunes" },
  { value: "FDO. PROMOCION", text: "Publicidad (fondo de promoción)" },
];

/** La excepción rige en el periodo "YYYY-MM" (desde y hasta inclusive; hasta vacío = sin término). */
export function vigenteEn(e: Pick<Excepcion, "desde" | "hasta">, periodo: string): boolean {
  return (!e.desde || e.desde <= periodo) && (!e.hasta || periodo <= e.hasta);
}

export function excepcionesVigentes(excepciones: Excepcion[], codigoContrato: number | string, periodo: string): Excepcion[] {
  return excepciones.filter((e) => String(e.codigoContrato) === String(codigoContrato) && vigenteEn(e, periodo));
}

/** "solo variable" se registra sobre el arriendo: afecta al fijo y al variable. */
function afecta(e: Excepcion, nombreDivision: string): boolean {
  const n = normalizar(nombreDivision);
  if (e.tipo === "solo-variable") return n === "ALQUILER FIJO" || n === "ALQUILER VARIABLE";
  return e.concepto === "FDO. PROMOCION" ? n.startsWith("FDO. PROMOCION") : n === e.concepto;
}

/** Texto corto de la excepción para la glosa y las tablas. */
export function describirExcepcion(e: Excepcion): string {
  const vigencia = e.hasta ? ` hasta ${e.hasta}` : "";
  const valor =
    e.tipo === "descuento-pct"
      ? `descuento ${formatearNumero(e.valor * 100, 2)}%`
      : e.tipo === "descuento-monto"
      ? `descuento $${formatearNumero(e.valor)}`
      : e.tipo === "monto-pactado"
      ? `monto pactado $${formatearNumero(e.valor)}`
      : e.tipo === "no-cobrar"
      ? "no se cobra"
      : "solo variable, sin mínimo";
  return `${valor}${vigencia}${e.motivo ? `: ${e.motivo}` : ""}`;
}

/** Copia de las divisiones con los descuentos en %, "no cobrar" y "solo variable" aplicados. */
export function ajustarDivisiones<T extends DivisionCalculo>(divisiones: T[], vigentes: Excepcion[]): T[] {
  return divisiones.map((d) => {
    let ajustada: T = { ...d };
    for (const e of vigentes) {
      if (!afecta(e, d.nombre_division)) continue;
      if (e.tipo === "solo-variable") {
        if (normalizar(d.nombre_division) === "ALQUILER VARIABLE") ajustada = { ...ajustada, operador: "SIN MINIMO" };
        continue;
      }
      if (e.tipo !== "descuento-pct" && e.tipo !== "no-cobrar") continue;
      const factor = e.tipo === "no-cobrar" ? 0 : Math.max(0, 1 - (Number(e.valor) || 0));
      // El variable y la publicidad sobre el arriendo se cobran por %; los demás, por tarifa.
      const porPorcentaje = normalizar(d.nombre_division) === "ALQUILER VARIABLE" || promocionSobreAlquiler(d);
      if (porPorcentaje) ajustada = { ...ajustada, porcentaje: (Number(ajustada.porcentaje) || 0) * factor };
      else ajustada = { ...ajustada, minimo: (Number(ajustada.minimo) || 0) * factor };
    }
    return ajustada;
  });
}

export interface ItemConExcepcion extends ItemCalculado {
  /** Excepciones aplicadas al ítem, para mostrarlas en la revisión. */
  excepciones?: string[];
}

/** Aplica sobre los ítems ya calculados lo que no cambia la tarifa y deja la glosa explicada. */
export function aplicarExcepciones(items: ItemCalculado[], vigentes: Excepcion[]): ItemConExcepcion[] {
  return items.map((item) => {
    const propias = vigentes.filter((e) => afecta(e, item.division));
    if (!propias.length) return item;
    let monto = item.montoNeto;
    let montoUF = item.montoUF;
    for (const e of propias) {
      if (e.tipo === "descuento-monto") monto = Math.max(0, monto - (Number(e.valor) || 0));
      if (e.tipo === "monto-pactado") monto = Math.round(Number(e.valor) || 0);
      if (e.tipo === "solo-variable" && normalizar(item.division) === "ALQUILER FIJO") monto = 0;
      if (e.tipo !== "descuento-pct" && e.tipo !== "no-cobrar") montoUF = undefined;
    }
    const textos = propias.map(describirExcepcion);
    const resultado: ItemConExcepcion = {
      ...item,
      montoNeto: Math.round(monto),
      glosa: `${item.glosa} · ${textos.join(" · ")}`,
      excepciones: textos,
    };
    if (montoUF === undefined) delete resultado.montoUF;
    else resultado.montoUF = montoUF;
    // Lo que no se cobra no queda pendiente de ventas.
    if (propias.some((e) => e.tipo === "no-cobrar" || e.tipo === "monto-pactado")) delete resultado.pendiente;
    return resultado;
  });
}

/** Valida una excepción antes de guardarla. Devuelve el primer problema o "". */
export function validarExcepcion(e: Partial<Excepcion>): string {
  if (!e.tipo) return "Elige el tipo de excepción";
  if (e.tipo !== "solo-variable" && !e.concepto) return "Elige el concepto";
  if (e.tipo === "descuento-pct" && !(Number(e.valor) > 0 && Number(e.valor) <= 1)) return "El descuento debe estar entre 0% y 100%";
  if ((e.tipo === "descuento-monto" || e.tipo === "monto-pactado") && !(Number(e.valor) >= 0)) return "Indica el monto en pesos";
  if (!e.desde || !/^\d{4}-\d{2}$/.test(e.desde)) return "Indica desde qué mes rige";
  if (e.hasta && e.hasta < e.desde) return "El mes de término es anterior al de inicio";
  if (!String(e.motivo || "").trim()) return "Indica el motivo";
  if (!String(e.autorizadoPor || "").trim()) return "Indica quién la autorizó";
  return "";
}
